import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { GetSalesInsightsGroupedRequest, SalesInsightsCompareGroupedResponse, useSalesInsightsCompareGrouped } from '../../../../api/indicators';
import { UserContext } from '../../../../context/UserContext';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatPeriodLabel } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import ComparedPieBarChart, { ComparedPieBarChartData } from '../comparedCharts/ComparedPieBarChart';
import { TableChip } from '../../../../components/EnhancedTableToolbar';


const ComparedSalesByStaff = () => {
    const { t } = useTranslation();
    const { locale } = useContext(UserContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);
    const [ params, setParams ] = useState<GetSalesInsightsGroupedRequest>({
        fields: 'netSales',
        startDateTime: filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
        endDateTime:filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
        compareStartDateTime: filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate,
        compareEndDateTime:filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate,
        locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
        groupBy: 'staff'
    });

    const {
        data: compareGroupedSalesInsightsResponse,
        isLoading,
        isError,
        refetch: fetchSalesInsightsCompareGrouped
    } = useSalesInsightsCompareGrouped<SalesInsightsCompareGroupedResponse>(params, 'sales-insights/grouped');

    useEffect(() => {
        setParams(p => ({
            ...p,
            startDateTime: filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
            endDateTime:filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
            compareStartDateTime: filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate,
            compareEndDateTime: filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate,
            locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id
        }));
    }, [filters, dateRangeConfig, companyConfig]);

    useEffect(() => {
        fetchSalesInsightsCompareGrouped();
    }, [params, fetchSalesInsightsCompareGrouped]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsightsCompareGrouped();
        }
    }, [refreshDate, fetchSalesInsightsCompareGrouped]);

    function transformData(data: SalesInsightsCompareGroupedResponse | undefined): ComparedPieBarChartData[] {
        // no data
        if (!data || (!data?.salesInsightsGrouped && !data?.comparedSalesInsightsGrouped)) return [];

        // no comparison data
        if (!data.comparedSalesInsightsGrouped) {
            return data.salesInsightsGrouped.map(item => ({
                label: item.key,
                value: item.salesInsights.totalSalesIncTax ?? 0,
                comparedValue: 0
            }));
        }

        // no current data
        if (!data.salesInsightsGrouped) {
            return data.comparedSalesInsightsGrouped.map(item => ({
                label: item.key,
                value: 0,
                comparedValue: item.salesInsights.totalSalesIncTax ?? 0
            }));
        }

        // both periods have data
        const currentDataMap = new Map(
            data.salesInsightsGrouped.map(item => [item.key, item.salesInsights.netSalesIncTax])
        );
        
        const comparedDataMap = new Map(
            data.comparedSalesInsightsGrouped.map(item => [item.key, item.salesInsights.netSalesIncTax])
        );
        
        const allKeys = new Set([
            ...(data.salesInsightsGrouped?.map(item => item.key) || []),
            ...(data.comparedSalesInsightsGrouped?.map(item => item.key) || [])
        ]);
        
        return Array.from(allKeys).map(key => ({
            label: key,
            value: currentDataMap.get(key) ?? 0,
            comparedValue: comparedDataMap.get(key) ?? 0,
        }));
    }
    const currentChip: TableChip = useMemo(() => 
            ({
                label: `${formatPeriodLabel(
                    new Date(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                    locale
                  )} - ${formatPeriodLabel(new Date(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                  labelType: 'period'
            }), [filters.periodFrom, filters.periodTo]);
    
            const compareChip: TableChip = useMemo(() => 
            ({
                label: `${formatPeriodLabel(
                    new Date(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                    locale
                  )} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                  labelType: 'periodCompare'
            }), [filters.comparePeriodFrom, filters.comparePeriodTo]);
    

    return (
        <Box>
            <ComparedPieBarChart
                title={t('screens.dashboarding.charts.netSalesByStaff')}
                description={t('screens.dashboarding.charts.includingTax')}
                data={transformData(compareGroupedSalesInsightsResponse)}
                currency={compareGroupedSalesInsightsResponse?.currency}
                pieLabels={[
                    `${formatPeriodLabel(new Date(filters.periodFrom ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.periodTo ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                    `${formatPeriodLabel(new Date(filters.comparePeriodFrom ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`
                ]}
                locale={locale}
                isLoading={isLoading}
                error={isError}
                height={560}
                reportName={t('screens.dashboarding.charts.netSalesByStaff')}
                reportLink={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByEmployee.aspx`}
                chips={[currentChip, compareChip]}
            />
        </Box>
    )
}

export default ComparedSalesByStaff;
